import { chakra, Box, Text } from "@chakra-ui/react";
import type { SystemStyleObject } from "@chakra-ui/react";
import BigNumber from "bignumber.js";
import React from "react";

import type { TokenInfo } from "types/api/token";
import type {
  Transaction,
  WrappedTransactionFields,
} from "types/api/transaction";

import config from "configs/app";
import getCurrencyValue from "lib/getCurrencyValue";
import {
  formatUsdValue,
  removeTrailingZeros,
  formatWithEllipsis,
} from "lib/formatNumber";
import { currencyUnits } from "lib/units";
import { Skeleton } from "toolkit/chakra/skeleton";
import CurrencyValue from "ui/shared/CurrencyValue";
import TokenEntity, {
  getTokenDisplaySymbol,
} from "ui/shared/entities/token/TokenEntity";
import IconSvg from "ui/shared/IconSvg";
import { Tooltip } from "toolkit/chakra/tooltip";

interface Props {
  className?: string;
  justifyContent?: string;
  isLoading?: boolean;
  tx: Transaction | Pick<Transaction, WrappedTransactionFields>;
  withCurrency?: boolean;
  withUsd?: boolean;
  accuracy?: number;
  accuracyUsd?: number;
  showRate?: boolean; // 是否显示汇率信息，默认 false
  fixedDecimals?: number; // 如果设置，显示时截断精度，hover 时显示完整精度
  // Chakra UI 样式属性
  color?: string;
  fontSize?: string;
  fontWeight?: string;
}

const TxFee = ({
  className,
  tx,
  accuracy,
  accuracyUsd,
  isLoading,
  withCurrency = true,
  withUsd,
  showRate = false,
  justifyContent = "start",
  fixedDecimals,
  color,
  fontSize,
  fontWeight,
}: Props) => {
  // Morph ERC20 Gas Fee (type=127)
  if (tx.fee_token_id !== undefined && tx.fee_token_symbol && tx.fee_rate) {
    const feeTokenAddress = tx.fee_token_address || "";
    const displaySymbol = getTokenDisplaySymbol(
      feeTokenAddress,
      tx.fee_token_symbol!,
    );
    const token: TokenInfo<"ERC-20"> = {
      address: feeTokenAddress,
      type: "ERC-20",
      symbol: tx.fee_token_symbol || displaySymbol || "",
      name: tx.fee_token_name || null,
      decimals: tx.fee_token_decimals || null,
      holders: null,
      exchange_rate: null,
      total_supply: null,
      icon_url: null,
      circulating_market_cap: null,
    };

    // 链上公式：actualFee = ceil((gas_used x gas_price + l1_fee) x token_scale / fee_rate)
    const tokenRate = BigNumber(tx.fee_rate); // tokenRate (fee_rate)
    const tokenScale = BigNumber(tx.token_scale || "1");
    const tokenDecimals = parseInt(tx.fee_token_decimals || "18");
    const gasPrice = BigNumber(tx.gas_price || "0");
    const gasUsed = BigNumber(tx.gas_used || "0");
    const l1Fee = BigNumber(tx.l1_fee || "0");

    // 实际费用分子 = (gas_used × gas_price + l1_fee) × token_scale
    const actualFeeNumerator = gasUsed
      .multipliedBy(gasPrice)
      .plus(l1Fee)
      .multipliedBy(tokenScale);
    // 实际费用 = ceil(分子 / fee_rate)，结果为 token 最小单位
    // 用 dividedToIntegerBy + 余数判断实现精确向上取整：
    // 若先用 dividedBy() 再 integerValue(ROUND_CEIL)，BigNumber 默认只保留 20 位小数，
    // 极端余数会在除法阶段被抹掉，导致结果少 1
    const actualFeeQuotient = actualFeeNumerator.dividedToIntegerBy(tokenRate);
    const actualFeeMinUnit = actualFeeNumerator.modulo(tokenRate).isZero()
      ? actualFeeQuotient
      : actualFeeQuotient.plus(1);

    // 转换为人类可读的值
    const tokenAmount = actualFeeMinUnit.dividedBy(
      BigNumber(10).pow(tokenDecimals),
    );

    // 计算汇率显示（1 TOKEN = X ETH）
    // 推导：tokenAmount (最小单位) = (ethAmount_wei * tokenScale) / tokenRate
    // 1 token (人类可读) = 10^tokenDecimals (最小单位)
    // 需要多少 wei？ 10^tokenDecimals = (X_wei * tokenScale) / tokenRate
    // X_wei = 10^tokenDecimals * tokenRate / tokenScale
    // X_ETH = X_wei / 10^18 = (10^tokenDecimals * tokenRate) / tokenScale / 10^18
    // 保持完整精度，不在计算过程中截断
    const ethPerToken = BigNumber(10)
      .pow(tokenDecimals)
      .multipliedBy(tokenRate)
      .dividedBy(tokenScale)
      .dividedBy(BigNumber(10).pow(18));

    // 格式化汇率，去掉尾随的 0
    // 使用 toFixed() 确保精度，然后去除尾随的 0 和多余的小数点
    const formattedRate = removeTrailingZeros(
      ethPerToken.toFixed(18, BigNumber.ROUND_DOWN),
    );

    // 计算美元价格（使用 tokenAmount 转换为 ETH，再乘以 exchange_rate）
    let usdValue: string | null = null;
    if (withUsd && "exchange_rate" in tx && tx.exchange_rate) {
      // tokenAmount (人类可读) * ethPerToken = ETH 等价值
      const feeInEth = tokenAmount.multipliedBy(ethPerToken);
      const exchangeRate = BigNumber(tx.exchange_rate);
      const usd = feeInEth.multipliedBy(exchangeRate);
      // 使用智能格式化，根据数值大小自动选择精度
      usdValue = formatUsdValue(usd, accuracyUsd);
    }

    // 完整精度的值（后端返回的全部数据），用于 hover 显示和详情页
    // 使用足够大的精度确保完整显示
    const maxPrecision = Math.max(tokenDecimals, 18);
    const fullPrecisionValue = removeTrailingZeros(
      tokenAmount.toFixed(maxPrecision),
    );

    // 格式化显示的值
    // 如果指定了 fixedDecimals（列表页），按照 fixedDecimals 精度向上取整显示
    // 否则（详情页）显示完整精度
    let displayValue: string;
    if (fixedDecimals !== undefined) {
      // 按照 fixedDecimals 精度向上取整
      const roundedValue = tokenAmount.decimalPlaces(
        fixedDecimals,
        BigNumber.ROUND_CEIL,
      );
      displayValue = removeTrailingZeros(roundedValue.toFixed(fixedDecimals));
    } else {
      displayValue = fullPrecisionValue;
    }

    // 如果显示值与完整值不同（列表页向上取整后），则在 hover 时显示完整精度
    const shouldShowTooltip = displayValue !== fullPrecisionValue;

    return (
      <Box
        className={className}
        color={color}
        fontSize={fontSize}
        fontWeight={fontWeight}
      >
        <Skeleton
          whiteSpace="pre-wrap"
          wordBreak="break-word"
          loading={isLoading}
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          justifyContent={justifyContent}
        >
          {/* 只在最后显示时才格式化，确保计算过程使用完整精度 */}
          {/* 如果有 fixedDecimals，在 hover 时显示完整精度 */}
          {shouldShowTooltip ? (
            <Tooltip
              content={`${fullPrecisionValue} ${displaySymbol}`}
              positioning={{ placement: "top" }}
            >
              <span
                style={{ cursor: "help", textDecoration: "underline dotted" }}
              >
                {displayValue}{" "}
              </span>
            </Tooltip>
          ) : (
            <span>{displayValue} </span>
          )}
          <TokenEntity token={token} noCopy onlySymbol w="auto" ml={1} />
          {usdValue && (
            <chakra.span color="text_secondary" ml={1}>
              (${usdValue})
            </chakra.span>
          )}
          {fixedDecimals && showRate && (
            <Tooltip
              content={
                "Transaction Fee is rounded up to the smallest unit of the fee token, so it may be slightly larger than Total fee."
              }
              positioning={{ placement: "top" as any }}
            >
              <IconSvg name="info" boxSize={4} />
            </Tooltip>
          )}
        </Skeleton>
        {showRate && (
          <Skeleton
            loading={isLoading}
            mt={1}
            display="flex"
            alignItems="center"
            justifyContent={justifyContent}
            color="text_secondary"
            fontSize="sm"
          >
            {/* 汇率显示已在 formattedRate 变量中格式化 */}
            <span>
              ( {displaySymbol}/{currencyUnits.ether} Rate: 1 {displaySymbol} ={" "}
              {formattedRate} {currencyUnits.ether}{" "}
            </span>
            <Tooltip
              content="Based on oracle price at Tx timestamp"
              positioning={{ placement: "top" }}
              // hasArrow
            >
              <Box display="inline-flex" ml={1} cursor="help">
                <IconSvg name="info" boxSize={4} />
              </Box>
            </Tooltip>
            <span>)</span>
          </Skeleton>
        )}
      </Box>
    );
  }

  if ("celo" in tx && tx.celo?.gas_token) {
    const token = tx.celo.gas_token;
    const { valueStr, usd } = getCurrencyValue({
      value: tx.fee.value || "0",
      exchangeRate: token.exchange_rate,
      decimals: token.decimals,
      accuracy,
      accuracyUsd,
    });
    const usdFormatted = usd;
    return (
      <Skeleton
        whiteSpace="pre-wrap"
        wordBreak="break-word"
        justifyContent={justifyContent || "start"}
        loading={isLoading}
        display="flex"
        flexWrap="wrap"
        className={className}
      >
        <span>{valueStr} </span>
        <TokenEntity token={token} noCopy onlySymbol w="auto" ml={1} />
        {usdFormatted && withUsd && (
          <chakra.span color="text.secondary"> (${usdFormatted})</chakra.span>
        )}
      </Skeleton>
    );
  }

  if ("stability_fee" in tx && tx.stability_fee) {
    const token = tx.stability_fee.token;
    const { valueStr, usd } = getCurrencyValue({
      value: tx.stability_fee.total_fee,
      exchangeRate: token.exchange_rate,
      decimals: token.decimals,
      accuracy,
      accuracyUsd,
    });
    const usdFormatted = usd;

    return (
      <Skeleton
        whiteSpace="pre"
        loading={isLoading}
        display="flex"
        justifyContent={justifyContent || "start"}
        className={className}
      >
        <span>{valueStr} </span>
        {valueStr !== "0" && (
          <TokenEntity token={token} noCopy onlySymbol w="auto" ml={1} />
        )}
        {usdFormatted && withUsd && (
          <chakra.span color="text.secondary"> (${usdFormatted})</chakra.span>
        )}
      </Skeleton>
    );
  }

  const showCurrency =
    withCurrency && !config.UI.views.tx.hiddenFields?.fee_currency;

  return (
    <CurrencyValue
      value={tx.fee.value}
      currency={showCurrency ? currencyUnits.ether : ""}
      exchangeRate={withUsd && "exchange_rate" in tx ? tx.exchange_rate : null}
      accuracy={accuracy}
      accuracyUsd={accuracyUsd}
      flexWrap="wrap"
      className={className}
      isLoading={isLoading}
    />
  );
};

export default TxFee; // React.memo(chakra(TxFee));
